import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import PropTypes from 'prop-types'
import React from 'react'

import { createAtHomeBooking, searchAtHomeParents } from 'api/atHome'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

import {
  apiErrorMessage,
  fmtDate,
  fmtMoney,
  fmtTime,
  gradeMismatch
} from './format'

/**
 * "Book a new trial" from Alfred: pick a parent (live search over every
 * main-system parent by name or phone), one of their children, and
 * send the payment link; the booking confirms itself when the parent pays.
 */
const BookTrialDialog = ({ slot, open, onClose, onBooked }) => {
  const [query, setQuery] = React.useState('')
  const [options, setOptions] = React.useState([])
  const [searching, setSearching] = React.useState(false)
  const [parent, setParent] = React.useState(null)
  const [child, setChild] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    if (!open) return
    setQuery('')
    setOptions([])
    setParent(null)
    setChild('')
    setError('')
  }, [open])

  React.useEffect(() => {
    if (query.trim().length < 2) return undefined
    let active = true
    setSearching(true)
    const t = setTimeout(() => {
      searchAtHomeParents(query.trim())
        .then((res) => active && setOptions(res))
        .catch(() => active && setOptions([]))
        .finally(() => active && setSearching(false))
    }, 300)
    return () => {
      active = false
      clearTimeout(t)
    }
  }, [query])

  const children = parent?.children || []
  const canBook = slot && parent && child && !busy

  const submit = async () => {
    setBusy(true)
    try {
      await createAtHomeBooking({
        slot: slot.uuid,
        parent: parent.uuid,
        child,
        // No checkbox in Alfred (owner, 2026-10-14): the payment-link message
        // itself tells the parent an adult must be at home, and paying is the
        // parent's acceptance of it.
        adult_presence_acknowledged: true,
        payment_status: 'link_sent'
      })
      eventAlert(
        'Payment link sent on WhatsApp. Nothing is booked yet: the slot stays open and the first payment takes it.',
        constants.SUCCESS
      )
      onBooked()
      onClose()
    } catch (err) {
      // The server's reason (grade rule, slot taken…) stays visible in the dialog.
      setError(apiErrorMessage(err, 'Could not book this trial.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Book a trial</DialogTitle>
      <DialogContent>
        {slot && (
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {slot.categoryName} · {slot.teacherName} · {fmtDate(slot.date)},{' '}
            {fmtTime(slot.start_time)} · {slot.apartmentName} ·{' '}
            {fmtMoney(slot.price)}
          </Typography>
        )}
        <Autocomplete
          options={options}
          loading={searching}
          value={parent}
          onChange={(e, v) => {
            setParent(v)
            setError('')
            // Preselect the only child, but not into a slot they cannot take.
            const only = v && v.children.length === 1 ? v.children[0] : null
            setChild(only && !gradeMismatch(only, slot?.grade) ? only.uuid : '')
          }}
          onInputChange={(e, v) => setQuery(v)}
          getOptionLabel={(o) => (o ? `${o.name} · ${o.phone}` : '')}
          getOptionSelected={(o, v) => o.uuid === v.uuid}
          noOptionsText={
            query.trim().length < 2
              ? 'Type a name or phone number'
              : 'No parent found'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Parent (name or phone)"
              margin="normal"
              fullWidth
              autoFocus
            />
          )}
        />
        <TextField
          select
          label="Child"
          value={child}
          onChange={(e) => setChild(e.target.value)}
          margin="normal"
          fullWidth
          disabled={!parent}
          helperText={
            parent && children.length === 0
              ? 'This parent has no child on record yet. Add one under HQ → Users.'
              : ' '
          }
        >
          {children.map((c) => {
            const why = gradeMismatch(c, slot?.grade)
            return (
              <MenuItem key={c.uuid} value={c.uuid} disabled={Boolean(why)}>
                {c.name}
                {c.grade ? ` · grade ${c.grade}` : ''}
                {why ? ` — ${why}` : ''}
              </MenuItem>
            )
          })}
        </TextField>
        {error && (
          <Typography variant="body2" color="error" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 16 }}
        >
          The parent gets a payment link on WhatsApp. Nothing is held: the slot
          stays open to everyone and the first payment books it. If another
          family pays first, this link stops working.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          onClick={submit}
          color="primary"
          variant="contained"
          disabled={!canBook}
        >
          Send payment link
        </Button>
      </DialogActions>
    </Dialog>
  )
}

BookTrialDialog.propTypes = {
  slot: PropTypes.object,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBooked: PropTypes.func.isRequired
}

export default BookTrialDialog
